/**
 * train-lib.ts — Logic thuần cho màn Dạy nhân viên AI (khung Kiểm tra hỏi đáp, Hỏi đáp, Phân tích).
 */
import { i18n } from '@/lib/i18n'

/** "Câu hỏi tương tự": mỗi dòng một câu — bỏ trống, bỏ trùng (không phân biệt hoa thường), bỏ trùng câu chuẩn. */
export function parseQuestionLines(text: string, canonical?: string | null): string[] {
  const seen = new Set<string>()
  if (canonical?.trim()) seen.add(canonical.trim().toLowerCase())
  const out: string[] = []
  for (const raw of text.split('\n')) {
    const q = raw.trim().replace(/\s+/g, ' ')
    if (!q || seen.has(q.toLowerCase())) continue
    seen.add(q.toLowerCase())
    out.push(q)
  }
  return out
}

/** Bước đang chạy (ước lượng theo thời gian chờ — API trả kết quả một lần). */
export function busyStepLabel(elapsedMs: number): string {
  if (elapsedMs < 1_500) return i18n.t('aiTrainLib:trainLib.busy.think')
  if (elapsedMs < 5_000) return i18n.t('aiTrainLib:trainLib.busy.search')
  return i18n.t('aiTrainLib:trainLib.busy.compose')
}

/** Gợi ý khi trả lời chậm (sau 8 giây). */
export const SLOW_HINT_MS = 8_000

export const MATCH_LABELS: Record<string, string> = {
  get semantic() { return i18n.t('aiTrainLib:trainLib.match.semantic') },
  get keyword() { return i18n.t('aiTrainLib:trainLib.match.keyword') },
  get both() { return i18n.t('aiTrainLib:trainLib.match.both') },
}

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  get faq() { return i18n.t('aiTrainLib:trainLib.sourceType.faq') },
  get article() { return i18n.t('aiTrainLib:trainLib.sourceType.article') },
  get product() { return i18n.t('aiTrainLib:trainLib.sourceType.product') },
  get order() { return i18n.t('aiTrainLib:trainLib.sourceType.order') },
}

/** Màu theo độ liên quan: ≥ 60% tốt, 35–60% yếu, còn lại thấp. */
export function scoreTone(pct: number | null | undefined): 'success' | 'warning' | 'secondary' {
  if (pct == null) return 'secondary'
  if (pct >= 60) return 'success'
  if (pct >= 35) return 'warning'
  return 'secondary'
}

/** JSON gọn để hiện trong khối thu gọn (cắt bớt nếu quá dài). */
export function jsonPreview(value: unknown, max = 4_000): string {
  if (value === undefined) return ''
  const s = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  return s.length > max ? s.slice(0, max) + '\n…' : s
}

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v)
  return /[",\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** CSV (BOM UTF-8 để Excel mở đúng tiếng Việt). */
export function toCsv(rows: unknown[][]): string {
  return '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n'
}

/** Mẫu nhập hỏi đáp (3 cột + Danh mục tuỳ chọn). */
// Tệp mẫu nhập hỏi đáp: tiêu đề cột + dòng mẫu là DỮ LIỆU — backend (kb-qa-csv.ts) nhận diện cột bằng tên tiếng Việt → giữ nguyên.
export const QA_CSV_TEMPLATE = toCsv([
  ['Câu hỏi', 'Câu hỏi tương tự', 'Câu trả lời', 'Danh mục'], // i18n-ignore
  ['Phí giao hàng bao nhiêu?', 'Ship bao nhiêu tiền\nGiao hàng có mất phí không', 'Dạ, phí giao hàng toàn quốc là 30.000đ, miễn phí cho đơn từ 500.000đ ạ.', ''], // i18n-ignore
  ['Shop mở cửa mấy giờ?', 'Giờ làm việc\nMấy giờ đóng cửa', 'Dạ shop mở cửa 8h–21h tất cả các ngày trong tuần ạ.', ''], // i18n-ignore
])

export const ANALYTICS_STATUS_LABELS: Record<string, string> = {
  get answered() { return i18n.t('aiTrainLib:trainLib.analyticsStatus.answered') },
  get handoff() { return i18n.t('aiTrainLib:trainLib.analyticsStatus.handoff') },
  get skipped() { return i18n.t('aiTrainLib:trainLib.analyticsStatus.skipped') },
  get error() { return i18n.t('aiTrainLib:trainLib.analyticsStatus.error') },
}

/** Ngày YYYY-MM-DD theo giờ máy, lùi `days` ngày. */
export function isoDay(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function lastNDays(n: number, now = new Date()): { from: string; to: string } {
  const from = new Date(now)
  from.setDate(from.getDate() - (n - 1))
  return { from: isoDay(from), to: isoDay(now) }
}

/** Căn cứ trả lời dạng một dòng: "Phí ship (82%), Đổi trả (41%)". */
export function basisLine(basis: Array<{ title: string; score: number | null }>): string {
  return basis.map((b) => (b.score != null ? `${b.title} (${Math.round(b.score * 100)}%)` : b.title)).join(', ')
}

export function downloadText(content: string | Blob, filename: string, type = 'text/csv;charset=utf-8') {
  const blob = content instanceof Blob ? content : new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1_000)
}
